import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { BreadcrumbService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, Subject, from, of } from 'rxjs';
import { catchError, concatMap, finalize, map, mergeMap, switchMap, takeUntil, tap, timeout } from 'rxjs/operators';
import { AuthService, getLocalizedValue } from '@card-software/nexus-utilities';
import { BuildPathFromCode, LocationNode, LocationPath, LocationType } from '../../../models/location';
import { LotItemType } from '../../../models/inventory/lot-item-search';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import {
  LotItemRelocationDto,
  LotItemRelocationLineStatus,
  LotItemRelocationScanResultDto,
  LotItemRelocationScanResultType,
  LotItemRelocationStatus,
  RelocationWarehouseOption,
} from '../../../models/inventory/lot-item-relocation';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { LotItemRelocationProxyService } from '../../../proxies/inventory/lot-item-relocation.proxy.service';
import { BarcodeScannerService } from '../../../services/barcode-scanner.service';

type RelocationMode = 'WarehouseSelection' | 'Loading' | 'Completed' | 'DraftEmpty' | 'DraftCollecting' | 'DraftReady';

interface ScanOutcome {
  severity: 'success' | 'warn' | 'info' | 'error';
  title: string;
  detail?: string;
}

const SCAN_TIMEOUT_MS = 15000;
const DUPLICATE_WINDOW_MS = 400;
const MAX_QUEUED_SCANS = 25;
const LOT_ITEM_COUNT_CONCURRENCY = 4;
const QUICK_SCAN_LIMIT = 10;

@Component({
  selector: 'app-lot-item-relocation-details',
  templateUrl: './lot-item-relocation-details.component.html',
  styleUrl: './lot-item-relocation-details.component.scss',
  standalone: false,
})
export class LotItemRelocationDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly barcodeScannerService = inject(BarcodeScannerService);
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly lotItemProxyService = inject(LotItemProxyService);
  private readonly relocationProxyService = inject(LotItemRelocationProxyService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  @ViewChild('scanInput') scanInputRef?: ElementRef<HTMLInputElement>;

  private readonly destroy$ = new Subject<void>();
  private readonly scanQueue$ = new Subject<string>();
  private lastScanCode: string | null = null;
  private lastScanAt = 0;

  isLoading = signal(0);
  relocationId = signal<string | null>(null);
  relocation = signal<LotItemRelocationDto | null>(null);
  warehouseOptions = signal<RelocationWarehouseOption[]>([]);
  warehouseName = signal<string>('');
  historyCount = signal<number | null>(null);

  scanText = '';
  pendingScans = signal(0);
  lastScanOutcome = signal<ScanOutcome | null>(null);

  warehouseLotItemCodes = signal<string[]>([]);
  destinationTree = signal<TreeNode[]>([]);
  expandedKeys = signal<Set<string>>(new Set<string>());
  destinationFilter = '';
  isLoadingDestinations = signal(false);

  canEdit = computed(() => AuthService.hasRole(UserGroup.LotItemRelocationAdmin));
  isNewRecord = computed(() => !this.relocationId());

  mode = computed<RelocationMode>(() => {
    if (this.isNewRecord()) return 'WarehouseSelection';

    const current = this.relocation();
    if (!current) return 'Loading';
    if (current.status === LotItemRelocationStatus.Completed) return 'Completed';
    if (!current.lines.length) return 'DraftEmpty';
    if (!current.toLocationCode) return 'DraftCollecting';
    return 'DraftReady';
  });

  isDraft = computed(() => {
    const current = this.relocation();
    return !!current && current.status === LotItemRelocationStatus.Draft;
  });

  lines = computed(() => this.relocation()?.lines ?? []);
  readyCount = computed(() => this.lines().filter((line) => line.lineStatus === LotItemRelocationLineStatus.Ready).length);
  alreadyHereCount = computed(() => this.lines().filter((line) => line.lineStatus === LotItemRelocationLineStatus.AlreadyHere).length);
  allLinesAlreadyHere = computed(() => this.lines().length > 0 && this.readyCount() === 0);

  canConfirm = computed(() => this.canEdit() && this.mode() === 'DraftReady' && this.readyCount() > 0 && this.pendingScans() === 0);

  destinationPath = computed(() => {
    const current = this.relocation();
    return current?.toLocationCode ?? '';
  });

  quickScanCodes = computed(() => {
    const collected = new Set(this.lines().map((line) => line.lotItemNumber));
    return this.warehouseLotItemCodes().filter((code) => !collected.has(code));
  });

  private routeRelocationId?: string | null;

  ngOnInit(): void {
    this.subscribeToScanQueue();

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => this.onRouteRelocationIdChange(params.get('id')));
  }

  private onRouteRelocationIdChange(rawId: string | null): void {
    const id = rawId && rawId !== 'new' ? rawId : null;
    if (id === this.routeRelocationId) {
      this.syncBreadcrumb();
      return;
    }
    this.routeRelocationId = id;

    this.resetRelocationState();
    this.relocationId.set(id);
    this.syncBreadcrumb();

    if (id) {
      this.loadRelocation(id);
    } else {
      this.loadWarehouseOptions();
    }

    this.loadHistoryCount();
  }

  private resetRelocationState(): void {
    this.relocation.set(null);
    this.warehouseName.set('');
    this.destinationTree.set([]);
    this.warehouseLotItemCodes.set([]);
    this.lastScanOutcome.set(null);
  }

  ngAfterViewInit(): void {
    this.focusScanInput();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((value) => value + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((value) => value - 1);
  }

  private translate(key: string, fallback: string): string {
    const translated = this.translateService.instant(key);
    return translated === key ? fallback : translated;
  }

  private syncBreadcrumb(): void {
    this.breadcrumbService.setCurrent(this.isNewRecord() ? 'Common_New' : this.relocation()?.number);
  }

  private localizedName(location: LocationNode): string {
    const lang = this.translateService.currentLang;
    const name = location.name;
    if (!name) return location.code;
    return (lang === 'es' ? name.es : name.en) || name.en || name.es || location.code;
  }

  private loadWarehouseOptions(): void {
    this.startLoading();
    this.locationProxyService
      .getAll()
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const locations = response?.data ?? [];
          const warehouses = locations.filter((location) => location.type === LocationType.Warehouse);

          this.warehouseOptions.set(
            warehouses
              .map((warehouse) => ({
                locationCode: warehouse.locationCode,
                code: warehouse.code,
                name: this.localizedName(warehouse),
                storableLocationCount: locations.filter(
                  (location) => location.isStorable && location.locationCode.startsWith(`${warehouse.locationCode}.`),
                ).length,
                lotItemCount: null,
              }))
              .sort((a, b) => Number(this.isSelectableWarehouse(b)) - Number(this.isSelectableWarehouse(a))),
          );

          this.loadLotItemCounts(warehouses);
        },
        error: () => this.warehouseOptions.set([]),
      });
  }

  private loadLotItemCounts(warehouses: LocationNode[]): void {
    from(warehouses)
      .pipe(
        mergeMap(
          (warehouse) =>
            this.lotItemProxyService.search({ locationPaths: [BuildPathFromCode(warehouse.locationCode)] }, 1, 1).pipe(
              map((response) => ({ locationCode: warehouse.locationCode, count: response?.data?.totalCount ?? null })),
              catchError(() => of({ locationCode: warehouse.locationCode, count: null })),
            ),
          LOT_ITEM_COUNT_CONCURRENCY,
        ),
      )
      .subscribe(({ locationCode, count }) =>
        this.warehouseOptions.update((options) =>
          options.map((option) => (option.locationCode === locationCode ? { ...option, lotItemCount: count } : option)),
        ),
      );
  }

  private loadHistoryCount(): void {
    this.relocationProxyService
      .search({}, 1, 1)
      .pipe(catchError(() => of(null)))
      .subscribe((response) => this.historyCount.set(response?.data?.totalCount ?? null));
  }

  private loadRelocation(id: string): void {
    this.startLoading();
    this.relocationProxyService
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const loaded = response?.data ?? null;
          this.relocation.set(loaded);
          if (loaded) this.onRelocationLoaded(loaded);
        },
        error: () => this.redirectToWarehouseSelection(),
      });
  }

  private onRelocationLoaded(loaded: LotItemRelocationDto): void {
    this.syncBreadcrumb();
    this.loadWarehouseHeader(loaded.warehouseLocationCode);
    this.loadDestinationTree(loaded.warehouseLocationCode);
    this.loadQuickScanCodes(loaded);
    this.focusScanInput();
  }

  private loadQuickScanCodes(loaded: LotItemRelocationDto): void {
    this.lotItemProxyService
      .search({ locationPaths: [BuildPathFromCode(loaded.warehouseLocationCode)] }, 1, QUICK_SCAN_LIMIT)
      .pipe(catchError(() => of(null)))
      .subscribe((response) =>
        this.warehouseLotItemCodes.set((response?.data?.items ?? []).map((item) => item.number).filter((number) => !!number)),
      );
  }

  onQuickScan(code: string): void {
    if (!this.canScan()) return;
    this.enqueueScan(code);
    this.focusScanInput();
  }

  private loadWarehouseHeader(warehouseLocationCode: string): void {
    this.locationProxyService
      .getByCode(warehouseLocationCode)
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        const warehouse = response?.data;
        this.warehouseName.set(warehouse ? this.localizedName(warehouse) : '');
      });
  }

  private loadDestinationTree(warehouseLocationCode: string): void {
    this.isLoadingDestinations.set(true);
    this.locationProxyService
      .getAllChildrenLocations(warehouseLocationCode)
      .pipe(finalize(() => this.isLoadingDestinations.set(false)))
      .subscribe({
        next: (response) => {
          const locations = (response?.data ?? []).filter((location) => location.locationCode.startsWith(warehouseLocationCode));
          const tree = this.buildTree(locations);
          this.destinationTree.set(tree);
          this.expandedKeys.set(new Set(tree.map((node) => node.key as string)));
          this.revealSelectedDestination();
        },
        error: () => this.destinationTree.set([]),
      });
  }

  private buildTree(locations: LocationNode[]): TreeNode[] {
    const nodeByCode = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    const ordered = [...locations].sort((a, b) => a.locationCode.localeCompare(b.locationCode));

    ordered.forEach((location) => {
      nodeByCode.set(location.locationCode, {
        key: location.locationCode,
        label: location.code,
        data: location,
        selectable: location.isStorable,
        children: [],
      });
    });

    ordered.forEach((location) => {
      const node = nodeByCode.get(location.locationCode)!;
      const parentCode = location.locationCode.substring(0, location.locationCode.lastIndexOf('.'));
      const parent = parentCode ? nodeByCode.get(parentCode) : undefined;
      if (parent) {
        parent.children!.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  }

  isExpanded(node: TreeNode): boolean {
    return this.expandedKeys().has(node.key as string);
  }

  toggleNode(node: TreeNode): void {
    const key = node.key as string;
    this.expandedKeys.update((keys) => {
      const next = new Set(keys);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  private revealSelectedDestination(): void {
    const target = this.relocation()?.toLocationCode;
    if (!target) return;

    const segments = target.split('.');
    const ancestors: string[] = [];
    for (let index = 1; index < segments.length; index += 1) {
      ancestors.push(segments.slice(0, index).join('.'));
    }

    this.expandedKeys.update((keys) => new Set([...keys, ...ancestors]));
  }

  getLocation(node: TreeNode): LocationNode {
    return node.data as LocationNode;
  }

  getLocationName(node: TreeNode): string {
    return this.localizedName(this.getLocation(node));
  }

  getLocationTypeLabel(node: TreeNode): string {
    return LocationType[this.getLocation(node).type] ?? '';
  }

  onDestinationFilterChange(): void {
    if (!this.destinationFilter.trim()) return;
    this.expandedKeys.update((keys) => new Set([...keys, ...this.collectKeys(this.destinationTree())]));
  }

  private collectKeys(nodes: TreeNode[]): string[] {
    return nodes.flatMap((node) => [node.key as string, ...this.collectKeys(node.children ?? [])]);
  }

  isDestinationVisible(node: TreeNode): boolean {
    const term = this.destinationFilter.trim().toLowerCase();
    if (!term) return true;

    const location = this.getLocation(node);
    if (location.code.toLowerCase().includes(term) || this.getLocationName(node).toLowerCase().includes(term)) return true;

    return (node.children ?? []).some((child) => this.isDestinationVisible(child));
  }

  isSelectedDestination(node: TreeNode): boolean {
    return this.getLocation(node).locationCode === this.relocation()?.toLocationCode;
  }

  isSelectableWarehouse(option: RelocationWarehouseOption): boolean {
    return (option.storableLocationCount ?? 0) > 0;
  }

  selectWarehouse(option: RelocationWarehouseOption): void {
    if (!this.canEdit() || !this.isSelectableWarehouse(option)) return;

    this.startLoading();
    this.relocationProxyService
      .create({ warehouseLocationCode: option.locationCode })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const created = response?.data;
          if (!created) return;

          this.routeRelocationId = created.id;
          this.relocation.set(created);
          this.relocationId.set(created.id);
          this.warehouseName.set(option.name);
          this.loadDestinationTree(created.warehouseLocationCode);
          this.loadQuickScanCodes(created);
          this.focusScanInput();

          this.router
            .navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { id: created.id },
              replaceUrl: true,
            })
            .then(() => this.syncBreadcrumb());
        },
      });
  }

  changeWarehouse(): void {
    this.confirmationService.confirm({
      header: this.translate('Lot_Item_Relocation_Change_Warehouse', 'Change warehouse'),
      message: this.translate(
        'Lot_Item_Relocation_Change_Warehouse_Message',
        'The scanned items stay on this draft. You can resume it from the movement history.',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate('Common_Continue', 'Continue'),
      rejectLabel: this.translate('Common_Cancel', 'Cancel'),
      key: 'main',
      closable: false,
      accept: () => this.redirectToWarehouseSelection(),
    });
  }

  private redirectToWarehouseSelection(): void {
    this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { id: 'new' },
        replaceUrl: true,
      })
      .then(() => this.syncBreadcrumb());
  }

  focusScanInput(): void {
    setTimeout(() => {
      const element = this.scanInputRef?.nativeElement;
      if (!element) return;
      element.focus({ preventScroll: true });
      element.select();
    });
  }

  onScanBlur(event: FocusEvent): void {
    const next = event.relatedTarget as HTMLElement | null;
    if (!next || next === document.body) this.focusScanInput();
  }

  onScanSubmit(): void {
    const raw = this.scanText.trim();
    if (!raw || !this.canScan()) return;

    const code = this.barcodeScannerService.extractRollNumber(raw) || raw;
    this.scanText = '';
    this.focusScanInput();
    this.enqueueScan(code);
  }

  onQrCodeScanned(code: string): void {
    if (code && this.canScan()) this.enqueueScan(code);
  }

  canScan(): boolean {
    return this.canEdit() && this.isDraft() && this.pendingScans() < MAX_QUEUED_SCANS;
  }

  private enqueueScan(code: string): void {
    const now = Date.now();
    if (this.lastScanCode === code && now - this.lastScanAt < DUPLICATE_WINDOW_MS) return;

    this.lastScanCode = code;
    this.lastScanAt = now;
    this.scanQueue$.next(code);
  }

  private subscribeToScanQueue(): void {
    this.scanQueue$
      .pipe(
        tap(() => this.pendingScans.update((value) => value + 1)),
        concatMap((code) =>
          this.runScan(code).pipe(
            timeout(SCAN_TIMEOUT_MS),
            catchError((error: HttpErrorResponse) => {
              this.showScanFailure(error);
              return EMPTY;
            }),
            finalize(() => this.pendingScans.update((value) => value - 1)),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private runScan(code: string): Observable<unknown> {
    const id = this.relocationId();
    if (!id) return EMPTY;

    return this.relocationProxyService
      .scan(id, { code }, { skipErrorToast: true })
      .pipe(switchMap((response) => this.applyScanResult(response?.data ?? undefined)));
  }

  private applyScanResult(result?: LotItemRelocationScanResultDto): Observable<unknown> {
    if (!result) return EMPTY;

    if (result.resultType === LotItemRelocationScanResultType.LotItem) {
      this.relocation.set(result.relocation);
      const scanned = result.relocation.lines[result.relocation.lines.length - 1];
      const alreadyHere = scanned?.lineStatus === LotItemRelocationLineStatus.AlreadyHere;

      this.lastScanOutcome.set({
        severity: alreadyHere ? 'warn' : 'success',
        title: scanned?.lotItemNumber ?? '',
        detail: alreadyHere
          ? this.translate('Lot_Item_Relocation_Scan_Already_Here', 'Already at destination — will be skipped')
          : this.translate('Lot_Item_Relocation_Scan_Added', 'Added'),
      });
      return EMPTY;
    }

    return this.applyLocationScan(result);
  }

  private applyLocationScan(
    result: Extract<LotItemRelocationScanResultDto, { resultType: LotItemRelocationScanResultType.Location }>,
  ): Observable<unknown> {
    const current = this.relocation();
    if (!current) return EMPTY;

    if (result.path?.warehouseCode && result.path.warehouseCode !== current.warehouseCode) {
      this.lastScanOutcome.set({
        severity: 'error',
        title: result.code,
        detail: this.translate('Lot_Item_Relocation_Scan_Other_Warehouse', 'That location is in a different warehouse'),
      });
      return EMPTY;
    }

    if (result.code === current.toLocationCode) {
      this.lastScanOutcome.set({
        severity: 'info',
        title: result.code,
        detail: this.translate('Lot_Item_Relocation_Destination_Unchanged', 'Destination unchanged'),
      });
      return EMPTY;
    }

    return this.relocationProxyService.updateDestination(current.id, { locationCode: result.code }, { skipErrorToast: true }).pipe(
      tap((response) => {
        if (response?.data) this.relocation.set(response.data);

        const occupancy = result.occupancy !== undefined && result.occupancy !== null ? ` · ${result.occupancy}` : '';
        this.lastScanOutcome.set({
          severity: 'info',
          title: this.translate('Lot_Item_Relocation_Destination_Set', 'Destination set'),
          detail: `${result.code}${occupancy}`,
        });
      }),
    );
  }

  private showScanFailure(error: HttpErrorResponse): void {
    this.lastScanOutcome.set({
      severity: 'error',
      title: this.translate('Common_Error', 'Error'),
      detail: this.extractErrorMessage(error),
    });
    this.focusScanInput();
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    if (!error?.status || error.status >= 500) {
      return this.translate('Lot_Item_Relocation_Connection_Error', 'Connection problem — the scan was not recorded');
    }

    const localized = getLocalizedValue(this.translateService, error.error?.errorMessage);
    if (localized) return localized;

    return error.error?.title ?? error.error?.detail ?? this.translate('Common_Unexpected_Error', 'Unexpected error');
  }

  selectDestination(node: TreeNode): void {
    const location = this.getLocation(node);
    const current = this.relocation();
    if (!current || !location.isStorable || !this.canEdit() || !this.isDraft()) return;

    this.startLoading();
    this.relocationProxyService
      .updateDestination(current.id, { locationCode: location.locationCode })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response?.data) this.relocation.set(response.data);
          this.focusScanInput();
        },
      });
  }

  removeLine(lotItemNumber: string): void {
    const current = this.relocation();
    if (!current || !this.canEdit() || !this.isDraft()) return;

    this.startLoading();
    this.relocationProxyService
      .removeLine(current.id, lotItemNumber)
      .pipe(
        switchMap(() => this.relocationProxyService.getById(current.id)),
        finalize(() => this.stopLoading()),
      )
      .subscribe({
        next: (response) => {
          if (response?.data) this.relocation.set(response.data);
          if (this.lastScanOutcome()?.title === lotItemNumber) this.lastScanOutcome.set(null);
          this.focusScanInput();
        },
      });
  }

  confirmRelocation(): void {
    const current = this.relocation();
    if (!current || !this.canConfirm()) return;

    this.confirmationService.confirm({
      header: this.translate('Lot_Item_Relocation_Confirm_Header', 'Confirm relocation'),
      message: `${this.readyCount()} ${this.translate('Lot_Item_Relocation_Lot_Items', 'Lot Items')} → ${current.toLocationCode}`,
      icon: 'pi pi-arrow-right',
      acceptLabel: this.translate('Common_Confirm', 'Confirm'),
      rejectLabel: this.translate('Common_Cancel', 'Cancel'),
      key: 'main',
      closable: false,
      accept: () => this.runConfirm(current.id),
    });
  }

  private runConfirm(id: string): void {
    this.startLoading();
    this.relocationProxyService
      .confirm(id, { skipErrorToast: true })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response?.data) this.relocation.set(response.data);
        },
        error: (error: HttpErrorResponse) => this.showConfirmConflict(error),
      });
  }

  private showConfirmConflict(error: HttpErrorResponse): void {
    this.confirmationService.confirm({
      header: this.translate('Lot_Item_Relocation_Conflict_Header', 'Items changed since they were scanned'),
      message: this.extractErrorMessage(error),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate('Lot_Item_Relocation_Conflict_Review', 'Review'),
      rejectVisible: false,
      key: 'main',
      closable: false,
      accept: () => this.loadRelocation(this.relocationId()!),
    });
  }

  getLotItemTypeLabel(type?: LotItemType): string {
    switch (type) {
      case LotItemType.Roll:
        return this.translate('Lot_Item_Type_Roll', 'Roll');
      case LotItemType.Chemical:
        return this.translate('Lot_Item_Type_Chemical', 'Chemical');
      default:
        return this.translate('Lot_Item_Type_Lot_Item', 'Lot Item');
    }
  }

  locationPathToText(path: LocationPath): string {
    return [path.warehouseCode, path.zoneCode, path.aisleCode, path.rackCode, path.bayCode, path.levelCode, path.binCode]
      .filter(Boolean)
      .join(' · ');
  }

  movedCount(): number {
    return this.lines().filter((line) => line.result === 0).length;
  }

  skippedCount(): number {
    return this.lines().filter((line) => line.result === 1).length;
  }

  redirectToHistory(): void {
    this.router.navigate(['/lot-item-relocation']);
  }
}
